/**
 * DataDenseCover — title + KPI chip grid. Designed for hyperscaler /
 * colo / IPP / financial-sponsor audiences who want the headline
 * numbers visible from page 1 without scrolling past a marketing-style
 * cover.
 *
 * KPI chips route through `resolveCoverChipsForReport` so report-type
 * intelligence, intent fallbacks, and variant subsets stay unified with
 * Classic / Hero / Minimal.
 */
import React from 'react';
import { PreparedForLine } from '../../PreparedForLine';
import { resolveCoverChipsForReport } from '../../../../lib/reportRendering/coverKpiChips';
import { CoverChipField } from './CoverChipField';
import { CoverCustomerLogo, customerLogoImgClass } from './CoverCustomerLogo';
import type { CoverProps } from './types';
import { DEFAULT_PRIMARY_COVER_LOGO_URL } from '../../../../lib/reportBranding';
import { roleColor } from '../../../../lib/reportRoleColors';
import { reportTypographySizes, reportHeadingInk } from '../../../../lib/reportSectionHeading';
import { resolveCoverTitle } from '../../../../lib/reportRendering/journalCoverData';
import { ReportPlatformCredit, platformCreditToneForTheme } from '../ReportPlatformCredit';

export const DataDenseCover: React.FC<CoverProps> = ({
  report, theme, branding, isAdmin,
  pickerOpen, onTogglePicker, onSelectArchetype,
  disclaimer,
}) => {
  // L-id-leak — never fall back to the raw Firestore `report.id`.
  const fallbackSiteWordmark = report.siteName?.trim() || 'Unnamed site';
  const effectiveBranding = branding ?? {
    audienceMode: 'internal',
    siteWordmark: fallbackSiteWordmark,
    primaryLogoUrl: theme.logoUrl ?? DEFAULT_PRIMARY_COVER_LOGO_URL,
    secondaryLogoUrl: null,
    primaryLogoAlt: theme.companyName ?? 'Primary logo',
    secondaryLogoAlt: 'Customer logo',
  };
  // P3 — title from the ONE shared resolver (config-label source of truth).
  const titleLabel = resolveCoverTitle(report);

  // report-cover consistency: derive the title size from the theme's
  // headingScale rather than a fixed `text-3xl`. The editorial h2
  // baseline is 1.5rem; data-dense's title read at text-3xl (1.875rem)
  // ≈ 1.25× the h2, so we keep that 1.25× ratio while letting
  // technical/compact presets scale it in step with section headings.
  const titleFontSize = `calc(${reportTypographySizes(theme).h2} * 1.25)`;

  const chips = resolveCoverChipsForReport(report, 'data-dense');
  const palette = theme.chartPalette.length > 0
    ? theme.chartPalette
    : [theme.primaryColor, theme.primaryColor, theme.primaryColor, theme.warningColor, theme.primaryColor];

  return (
    <div className="relative space-y-6 pb-14" data-testid="cover-data-dense" data-cover-variant="data-dense">
      <div className="flex items-start justify-between gap-4">
        <div
          className="text-base font-bold tracking-tighter"
          style={{ color: reportHeadingInk(theme) }}
          data-testid="cover-site-name-header"
        >
          {effectiveBranding.siteWordmark}
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="text-xs" style={{ fontFamily: 'var(--report-body-font, inherit)', color: roleColor(theme, 'muted') }}>{report.address}</div>
          {(effectiveBranding.primaryLogoUrl || effectiveBranding.secondaryLogoUrl) && (
            <div className="flex items-center gap-2" data-testid="cover-data-dense-logos" data-audience-mode={effectiveBranding.audienceMode}>
              {effectiveBranding.primaryLogoUrl && (
                <img
                  src={effectiveBranding.primaryLogoUrl}
                  alt={effectiveBranding.primaryLogoAlt}
                  className="h-5 max-w-[96px] object-contain"
                  data-testid="cover-data-dense-logo-primary"
                />
              )}
              {effectiveBranding.audienceMode === 'customer' && effectiveBranding.secondaryLogoUrl && (
                <>
                  <span className="mx-2 h-4 w-px" style={{ backgroundColor: roleColor(theme, 'hair') }} aria-hidden="true" />
                  <CoverCustomerLogo
                    src={effectiveBranding.secondaryLogoUrl}
                    alt={effectiveBranding.secondaryLogoAlt}
                    tone="on-light"
                    className={customerLogoImgClass('on-light', 'h-5 max-w-[96px]')}
                    data-testid="cover-data-dense-logo-secondary"
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <h1
        className="font-bold leading-tight tracking-tight"
        style={{
          fontFamily: theme.displayFont || theme.headingFont,
          color: reportHeadingInk(theme),
          fontSize: titleFontSize,
        }}
      >
        {titleLabel}
      </h1>

      <PreparedForLine
        report={report}
        isAdmin={isAdmin}
        pickerOpen={pickerOpen}
        onTogglePicker={onTogglePicker}
        onSelectArchetype={onSelectArchetype}
      />

      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-4"
        data-testid="cover-data-dense-kpis"
      >
        {chips.map((chip, index) => (
          <div
            key={chip.id}
            data-testid={`cover-data-dense-chip-${chip.id}`}
            className="border-l-2 pl-3 py-2"
            style={{ borderColor: palette[index % palette.length] ?? theme.primaryColor }}
          >
            <CoverChipField
              chip={chip}
              report={report}
              theme={theme}
              testIdSuffix={`data-dense-${chip.id}`}
              valueClassName="text-base font-bold tabular-nums"
            />
          </div>
        ))}
      </div>

      {/* GATEWAY POWER platform credit — left-aligned just BELOW the cover box
          (absolute, top: 100% + 3px): it lands in the cover shell's bottom
          padding on screen and in the empty rest of the page-tall #cover in
          print, so it adds no height and never meets the bottom-right
          disclaimer, whose width-dependent wrap collided with a bottom-left
          credit on phones. Rendered BEFORE the disclaimer with its margin
          zeroed: Tailwind v4 `space-y-*` margins every child but the last, and
          the disclaimer must stay the last child to keep its exact position. */}
      <ReportPlatformCredit
        tone={platformCreditToneForTheme(theme)}
        color={roleColor(theme, 'muted')}
        className="absolute left-0 top-[calc(100%_+_3px)]"
        style={{ marginBlock: 0 }}
        testId="cover-data-dense-platform-credit"
      />

      {/* CONFIDENTIAL DISCLAIMER — bottom-right footnote (COVER-CONTENT BATCH 4),
          rendered at 80% of the prior 10px size (→ 8px). */}
      <div
        className="absolute bottom-0 right-0 text-right text-[8px] leading-relaxed max-w-md"
        style={{ color: roleColor(theme, 'muted') }}
        data-testid="cover-data-dense-disclaimer"
      >
        {disclaimer}
      </div>
    </div>
  );
};
